import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { api, formatApiError } from '@/App';
import { 
  ArrowLeft, 
  Buildings, 
  Lightning, 
  Drop,
  User,
  Folder,
  FileText,
  Plus,
  Gauge,
  Trash,
  PencilSimple,
  TreeStructure,
  ArrowRight,
  Receipt,
  PaperPlaneTilt
} from '@phosphor-icons/react';
import PdfPreviewDialog from '@/components/PdfPreviewDialog';

const LEAD_STATUSES = ['Cold', 'Warm', 'Proposal Sent', 'Active', 'Inactive'];
const METER_TYPES = ['Water', 'Electricity'];
const PAYMENT_TYPES = ['Prepaid', 'Postpaid'];
const METER_STATUSES = ['Active', 'Disconnected', 'Pending', 'Decommissioned'];

const LEAD_COLORS = {
  'Cold': 'bg-blue-100 text-blue-700',
  'Warm': 'bg-yellow-100 text-yellow-700',
  'Proposal Sent': 'bg-purple-100 text-purple-700',
  'Active': 'bg-green-100 text-green-700',
  'Inactive': 'bg-gray-100 text-gray-700'
};

const AdminCompanyDetail = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [meterDialogOpen, setMeterDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [linkChildOpen, setLinkChildOpen] = useState(false);
  const [allCompanies, setAllCompanies] = useState([]);
  const [linkChildId, setLinkChildId] = useState('');
  const [linkingChild, setLinkingChild] = useState(false);
  const [statementPreview, setStatementPreview] = useState(false);
  const [emailingStatement, setEmailingStatement] = useState(false);

  const emailStatement = async () => {
    setEmailingStatement(true);
    try {
      const { data } = await api.post(`/statements/${companyId}/email`);
      toast.success(data.message);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setEmailingStatement(false); }
  };
  const [newMeter, setNewMeter] = useState({
    meter_number: '',
    meter_type: 'Electricity',
    payment_type: 'Postpaid',
    energy_supplier: '',
    billing_account_number: '',
    status: 'Active'
  });

  useEffect(() => {
    fetchCompany();
  }, [companyId]);

  const fetchCompany = async () => {
    try {
      const { data } = await api.get(`/companies/${companyId}`);
      setCompany(data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load company');
      navigate('/admin/companies');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.put(`/companies/${companyId}`, { lead_status: newStatus });
      setCompany(prev => ({ ...prev, lead_status: newStatus }));
      toast.success('Status updated');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/companies/${companyId}`);
      toast.success('Company and all related records deleted');
      navigate('/admin/companies');
    } catch (error) {
      toast.error(formatApiError(error));
      setDeleting(false);
    }
  };

  const openEdit = () => {
    setEditForm({
      company_name: company.company_name || '',
      company_email: company.company_email || '',
      company_phone: company.company_phone || '',
      physical_address: company.physical_address || '',
      vat_number: company.vat_number || '',
      lead_source: company.lead_source || '',
      account_type: company.account_type || 'business',
      residential_address: company.residential_address || '',
      id_number: company.id_number || '',
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(editForm).filter(([, v]) => v !== '' && v !== null && v !== undefined)
      );
      const { data } = await api.put(`/companies/${companyId}`, payload);
      setCompany((prev) => ({ ...prev, ...data }));
      toast.success('Company updated');
      setEditOpen(false);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleAddMeter = async (e) => {
    e.preventDefault();
    try {
      await api.post('/meters', { ...newMeter, company_id: companyId });
      toast.success('Meter added');
      setMeterDialogOpen(false);
      setNewMeter({
        meter_number: '',
        meter_type: 'Electricity',
        payment_type: 'Postpaid',
        energy_supplier: '',
        billing_account_number: '',
        status: 'Active'
      });
      fetchCompany();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const openLinkChild = async () => {
    setLinkChildId('');
    setLinkChildOpen(true);
    if (allCompanies.length === 0) {
      try {
        const { data } = await api.get('/companies');
        setAllCompanies(data || []);
      } catch (e) { console.error(e); }
    }
  };

  const handleLinkChild = async (e) => {
    e.preventDefault();
    if (!linkChildId) { toast.error('Pick a company to link'); return; }
    setLinkingChild(true);
    try {
      await api.post(`/companies/${companyId}/children`, { child_company_id: linkChildId });
      toast.success('Sub-account linked');
      setLinkChildOpen(false);
      fetchCompany();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLinkingChild(false);
    }
  };

  const handleUnlinkChild = async (childId) => {
    if (!window.confirm('Unlink this sub-account? The child company will remain in the system but will no longer be linked.')) return;
    try {
      await api.delete(`/companies/${companyId}/children/${childId}`);
      toast.success('Sub-account unlinked');
      fetchCompany();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Company Details">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#0055FF] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!company) return null;

  return (
    <AdminLayout title="Company Details">
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/companies">
              <Button variant="ghost" size="icon" className="rounded-none">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#0055FF]/10 flex items-center justify-center">
                <Buildings className="w-6 h-6 text-[#0055FF]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#0A192F]">{company.company_name}</h2>
                <p className="text-sm text-[#4B5563] font-mono">{company.company_id}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Select value={company.lead_status} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-40 rounded-none" data-testid="lead-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LEAD_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              className="rounded-none"
              onClick={() => setStatementPreview(true)}
              data-testid="view-statement-btn"
            >
              <Receipt className="w-4 h-4 mr-2" /> Statement
            </Button>
            <Button
              variant="outline"
              className="rounded-none"
              disabled={emailingStatement}
              onClick={emailStatement}
              data-testid="email-statement-btn"
            >
              <PaperPlaneTilt className="w-4 h-4 mr-2" /> {emailingStatement ? 'Sending…' : 'Email Statement'}
            </Button>
            <Button
              variant="outline"
              className="rounded-none"
              onClick={openEdit}
              data-testid="edit-company-header-btn"
            >
              <PencilSimple className="w-4 h-4 mr-2" /> Edit
            </Button>
            <Button
              variant="outline"
              className="rounded-none border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={() => setDeleteOpen(true)}
              data-testid="delete-company-header-btn"
            >
              <Trash className="w-4 h-4 mr-2" /> Delete
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Company Details */}
            <Card>
              <CardHeader className="border-b border-[#E5E7EB] pb-4">
                <CardTitle className="text-lg font-semibold text-[#0A192F]">Company Information</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-[#4B5563]">Email</p>
                    <p className="text-[#0A192F]">{company.company_email || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[#4B5563]">Phone</p>
                    <p className="text-[#0A192F]">{company.company_phone || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[#4B5563]">VAT Number</p>
                    <p className="text-[#0A192F] font-mono">{company.vat_number || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[#4B5563]">Lead Source</p>
                    <p className="text-[#0A192F]">{company.lead_source || '-'}</p>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-sm text-[#4B5563]">Physical Address</p>
                    <p className="text-[#0A192F]">{company.physical_address || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Meters */}
            <Card>
              <CardHeader className="border-b border-[#E5E7EB] pb-4 flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-semibold text-[#0A192F] flex items-center gap-2">
                  <Gauge className="w-5 h-5 text-[#0055FF]" />
                  Meters
                </CardTitle>
                <Dialog open={meterDialogOpen} onOpenChange={setMeterDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-[#0055FF] hover:bg-[#0044CC] rounded-none" data-testid="add-meter-btn">
                      <Plus className="w-4 h-4 mr-1" /> Add Meter
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add New Meter</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddMeter} className="space-y-4 mt-4">
                      <div>
                        <Label className="text-sm font-semibold">Meter Number *</Label>
                        <Input
                          value={newMeter.meter_number}
                          onChange={(e) => setNewMeter({ ...newMeter, meter_number: e.target.value })}
                          required
                          className="rounded-none mt-1 font-mono"
                          data-testid="meter-number-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-semibold">Type</Label>
                          <Select value={newMeter.meter_type} onValueChange={(v) => setNewMeter({ ...newMeter, meter_type: v })}>
                            <SelectTrigger className="rounded-none mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {METER_TYPES.map((t) => (
                                <SelectItem key={t} value={t}>{t}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-sm font-semibold">Payment Type</Label>
                          <Select value={newMeter.payment_type} onValueChange={(v) => setNewMeter({ ...newMeter, payment_type: v })}>
                            <SelectTrigger className="rounded-none mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {PAYMENT_TYPES.map((t) => (
                                <SelectItem key={t} value={t}>{t}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm font-semibold">Energy Supplier *</Label>
                        <Input
                          value={newMeter.energy_supplier}
                          onChange={(e) => setNewMeter({ ...newMeter, energy_supplier: e.target.value })}
                          required
                          placeholder="e.g., Eskom, City Power"
                          className="rounded-none mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold">Billing Account Number</Label>
                        <Input
                          value={newMeter.billing_account_number}
                          onChange={(e) => setNewMeter({ ...newMeter, billing_account_number: e.target.value })}
                          className="rounded-none mt-1 font-mono"
                        />
                      </div>
                      <div className="flex justify-end gap-3 pt-4">
                        <Button type="button" variant="outline" onClick={() => setMeterDialogOpen(false)} className="rounded-none">
                          Cancel
                        </Button>
                        <Button type="submit" className="bg-[#0055FF] hover:bg-[#0044CC] rounded-none" data-testid="submit-meter-btn">
                          Add Meter
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent className="pt-6">
                {company.meters && company.meters.length > 0 ? (
                  <div className="space-y-3">
                    {company.meters.map((meter) => (
                      <div key={meter.meter_id} className="flex items-center justify-between p-4 border border-[#E5E7EB]">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-[#0055FF]/10 flex items-center justify-center">
                            {meter.meter_type === 'Water' ? (
                              <Drop className="w-5 h-5 text-[#0055FF]" />
                            ) : (
                              <Lightning className="w-5 h-5 text-[#0055FF]" />
                            )}
                          </div>
                          <div>
                            <p className="font-mono font-medium text-[#0A192F]">{meter.meter_number}</p>
                            <p className="text-sm text-[#4B5563]">{meter.energy_supplier} • {meter.payment_type}</p>
                          </div>
                        </div>
                        <Badge className={meter.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                          {meter.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-[#4B5563] py-8">No meters registered</p>
                )}
              </CardContent>
            </Card>

            {/* Active Cases */}
            <Card>
              <CardHeader className="border-b border-[#E5E7EB] pb-4">
                <CardTitle className="text-lg font-semibold text-[#0A192F] flex items-center gap-2">
                  <Folder className="w-5 h-5 text-[#0055FF]" />
                  Active Cases
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {company.active_cases && company.active_cases.length > 0 ? (
                  <div className="space-y-3">
                    {company.active_cases.map((caseItem) => (
                      <Link key={caseItem.case_id} to={`/admin/cases/${caseItem.case_id}`} className="block">
                        <div className="flex items-center justify-between p-4 border border-[#E5E7EB] hover:border-[#0055FF] transition-colors">
                          <div>
                            <p className="font-medium text-[#0055FF]">{caseItem.service_type}</p>
                            <p className="text-sm text-[#4B5563]">{new Date(caseItem.created_at).toLocaleDateString()}</p>
                          </div>
                          <Badge>{caseItem.internal_status}</Badge>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-[#4B5563] py-8">No active cases</p>
                )}
              </CardContent>
            </Card>

            {/* Sub-Accounts (children) */}
            <Card data-testid="admin-children-section">
              <CardHeader className="border-b border-[#E5E7EB] pb-4 flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-semibold text-[#0A192F] flex items-center gap-2">
                  <TreeStructure className="w-5 h-5 text-[#0055FF]" />
                  Sub-Accounts {company.children?.length ? <span className="text-sm text-gray-400 font-normal">({company.children.length})</span> : null}
                </CardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-none"
                  onClick={openLinkChild}
                  data-testid="link-child-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Link Sub-Account
                </Button>
              </CardHeader>
              <CardContent className="pt-6">
                {company.parent && (
                  <div className="mb-4 p-3 border border-blue-200 bg-blue-50 flex items-center justify-between" data-testid="parent-indicator">
                    <div className="flex items-center gap-2 text-sm text-blue-900">
                      <span className="text-blue-600">Parent:</span>
                      <Link to={`/admin/companies/${company.parent.company_id}`} className="font-medium hover:underline">
                        {company.parent.company_name}
                      </Link>
                    </div>
                  </div>
                )}
                {company.children && company.children.length > 0 ? (
                  <div className="space-y-2">
                    {company.children.map((child) => (
                      <div key={child.company_id} className="flex items-center justify-between p-3 border border-[#E5E7EB] hover:border-[#0055FF] transition-colors" data-testid={`child-${child.company_id}`}>
                        <Link to={`/admin/companies/${child.company_id}`} className="flex items-center gap-3 flex-1">
                          <div className="w-8 h-8 bg-[#0055FF]/10 flex items-center justify-center">
                            <Buildings className="w-4 h-4 text-[#0055FF]" />
                          </div>
                          <div>
                            <p className="font-medium text-[#0A192F]">{child.company_name}</p>
                            <p className="text-xs text-gray-500">{child.account_type === 'personal' ? 'Personal' : 'Business'} · {child.company_email || '—'}</p>
                          </div>
                        </Link>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs rounded-none">{child.lead_status || 'Active'}</Badge>
                          <button
                            type="button"
                            onClick={() => handleUnlinkChild(child.company_id)}
                            className="text-gray-500 hover:text-red-600 text-xs underline"
                            data-testid={`unlink-child-${child.company_id}`}
                          >
                            Unlink
                          </button>
                          <Link to={`/admin/companies/${child.company_id}`} className="text-[#0055FF]">
                            <ArrowRight className="w-4 h-4" />
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-[#4B5563] py-6 text-sm">No sub-accounts linked. Use &quot;Link Sub-Account&quot; to attach an existing company as a child.</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contacts */}
            <Card>
              <CardHeader className="border-b border-[#E5E7EB] pb-4">
                <CardTitle className="text-lg font-semibold text-[#0A192F] flex items-center gap-2">
                  <User className="w-5 h-5 text-[#0055FF]" />
                  Contacts
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {company.proxies && company.proxies.length > 0 ? (
                  <div className="space-y-3">
                    {company.proxies.map((proxy) => (
                      <Link key={proxy.proxy_id} to={`/admin/proxies`} className="block">
                        <div className="p-3 border border-[#E5E7EB] hover:border-[#0055FF] transition-colors">
                          <p className="font-medium text-[#0A192F]">{proxy.first_name} {proxy.surname}</p>
                          <p className="text-sm text-[#4B5563]">{proxy.role}</p>
                          {proxy.is_primary && <Badge className="mt-2 bg-green-100 text-green-700">Primary</Badge>}
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-[#4B5563] py-4">No contacts linked</p>
                )}
              </CardContent>
            </Card>

            {/* Contracts */}
            <Card>
              <CardHeader className="border-b border-[#E5E7EB] pb-4">
                <CardTitle className="text-lg font-semibold text-[#0A192F] flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#0055FF]" />
                  Contracts
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {company.contracts && company.contracts.length > 0 ? (
                  <div className="space-y-3">
                    {company.contracts.map((contract) => (
                      <div key={contract.contract_id} className="p-3 border border-[#E5E7EB]">
                        <p className="font-medium text-[#0A192F]">{contract.contract_type}</p>
                        <p className="text-sm text-[#4B5563]">
                          Ends: {new Date(contract.end_date).toLocaleDateString()}
                        </p>
                        <Badge className={contract.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100'}>
                          {contract.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-[#4B5563] py-4">No contracts</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Dialog open={editOpen} onOpenChange={(open) => !savingEdit && setEditOpen(open)}>
        <DialogContent className="sm:max-w-[560px]" data-testid="edit-company-dialog">
          <DialogHeader>
            <DialogTitle>Edit Company</DialogTitle>
          </DialogHeader>
          {editForm && (
            <form onSubmit={handleSaveEdit} className="space-y-4 mt-4">
              <div>
                <Label className="text-sm font-semibold">Company Name *</Label>
                <Input
                  value={editForm.company_name}
                  onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                  required
                  className="rounded-none mt-1"
                  data-testid="edit-company-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold">Email</Label>
                  <Input
                    type="email"
                    value={editForm.company_email}
                    onChange={(e) => setEditForm({ ...editForm, company_email: e.target.value })}
                    className="rounded-none mt-1"
                    data-testid="edit-company-email"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold">Phone</Label>
                  <Input
                    value={editForm.company_phone}
                    onChange={(e) => setEditForm({ ...editForm, company_phone: e.target.value })}
                    className="rounded-none mt-1"
                    data-testid="edit-company-phone"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-semibold">Physical Address</Label>
                <Input
                  value={editForm.physical_address}
                  onChange={(e) => setEditForm({ ...editForm, physical_address: e.target.value })}
                  className="rounded-none mt-1"
                  data-testid="edit-company-address"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold">VAT Number</Label>
                  <Input
                    value={editForm.vat_number}
                    onChange={(e) => setEditForm({ ...editForm, vat_number: e.target.value })}
                    className="rounded-none mt-1 font-mono"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold">Lead Source</Label>
                  <Input
                    value={editForm.lead_source}
                    onChange={(e) => setEditForm({ ...editForm, lead_source: e.target.value })}
                    className="rounded-none mt-1"
                  />
                </div>
              </div>
              {editForm.account_type === 'personal' && (
                <>
                  <div>
                    <Label className="text-sm font-semibold">Residential Address</Label>
                    <Input
                      value={editForm.residential_address}
                      onChange={(e) => setEditForm({ ...editForm, residential_address: e.target.value })}
                      className="rounded-none mt-1"
                      data-testid="edit-company-residential"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold">ID Number</Label>
                    <Input
                      value={editForm.id_number}
                      onChange={(e) => setEditForm({ ...editForm, id_number: e.target.value })}
                      className="rounded-none mt-1 font-mono"
                      placeholder="13-digit SA ID"
                    />
                  </div>
                </>
              )}
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditOpen(false)} className="rounded-none">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={savingEdit}
                  className="bg-[#0055FF] hover:bg-[#0044CC] rounded-none"
                  data-testid="save-edit-company-btn"
                >
                  {savingEdit ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={(open) => !deleting && setDeleteOpen(open)}>
        <AlertDialogContent data-testid="delete-company-detail-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {company.company_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              The company, its service cases, meters, contracts, contact links and documents will be permanently deleted.
              <br /><br />
              <strong>Financial records will NOT be deleted</strong> — quotations, invoices and signed contracts are
              archived and retained for 5 years to comply with POPIA &amp; the Tax Administration Act. They will keep
              counting in your Reports &amp; Financials totals.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="cancel-delete-company-detail">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-company-detail"
            >
              {deleting ? 'Deleting...' : 'Delete Permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={linkChildOpen} onOpenChange={(o) => !linkingChild && setLinkChildOpen(o)}>
        <DialogContent className="sm:max-w-[480px]" data-testid="link-child-dialog">
          <DialogHeader>
            <DialogTitle>Link Sub-Account to {company.company_name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleLinkChild} className="space-y-4 mt-2">
            <div>
              <Label className="text-sm font-semibold">Pick an existing company *</Label>
              <Select value={linkChildId} onValueChange={setLinkChildId}>
                <SelectTrigger className="rounded-none mt-1" data-testid="link-child-select">
                  <SelectValue placeholder="Search a company…" />
                </SelectTrigger>
                <SelectContent className="max-h-72 overflow-y-auto">
                  {allCompanies
                    .filter(c =>
                      c.company_id !== companyId &&
                      c.parent_company_id !== companyId
                    )
                    .map(c => (
                      <SelectItem key={c.company_id} value={c.company_id}>
                        {c.company_name}
                        {c.parent_company_id && <span className="ml-2 text-xs text-orange-600">(already a child of {c.parent_company_name || 'another company'})</span>}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">If the company is already a child of someone else, linking here will reassign it.</p>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setLinkChildOpen(false)} className="rounded-none">Cancel</Button>
              <Button type="submit" disabled={linkingChild || !linkChildId} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-none" data-testid="confirm-link-child">
                {linkingChild ? 'Linking…' : 'Link Sub-Account'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <PdfPreviewDialog
        open={statementPreview}
        onOpenChange={(open) => !open && setStatementPreview(false)}
        url={statementPreview ? `/api/statements/${companyId}/pdf` : null}
        title={`Statement of account — ${company.company_name}`}
        filename={`Statement-${company.company_name}.pdf`}
      />
    </AdminLayout>
  );
};

export default AdminCompanyDetail;
